import { useMemo } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { AddIcon } from "@l2r-front/l2r-icons"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../constants/i18n"
import { STATUS_IN_PROGRESS } from "../../constants/status"
import { useCampaigns } from "../../hooks/queries/useCampaigns"

import * as Styled from "./CampaignSelector.styled"

export interface CampaignSelectorProps {
    campaignIds: { uuid: string }[]
    campaignType: string
    handleUpdateAttachedCampaigns: () => void
    onAddCampaignClick: () => void
    onChange: (campaignUuid: string) => void
    onClose: (e?: React.SyntheticEvent) => void
}

export const CampaignSelector = (props: CampaignSelectorProps) => {

    const {
        campaignIds,
        campaignType,
        onAddCampaignClick,
        onChange,
        onClose,
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const { data, isLoading, isError } = useCampaigns({ campaign_type: campaignType, status: STATUS_IN_PROGRESS }, { enabled: !!campaignType })
    const isMobile = useIsMobileDevice()

    const campaignsOptions = useMemo(() => {
        const options = []

        options.push(
            <Styled.AddCampaignButton key="campaign-create" color="primary" isMobile={isMobile} onClick={() => {
                onAddCampaignClick()
                if (onClose) {
                    onClose()
                }
            }}>
                <AddIcon color="action/active" />
                <Styled.ListItemText>
                    {t(I18N_NAMESPACE, "components.campaignSelector.createCampaign")}
                </Styled.ListItemText>
            </Styled.AddCampaignButton>,
        )
        if (isError) {
            options.push(<Styled.Typography key="error" color="error">
                {t(I18N_NAMESPACE, "components.campaignSelector.errorLoadingCampaigns")}
            </Styled.Typography>)
            return options
        }

        if (data?.length > 0) {
            data.forEach((campaign) => {
                options.push(<Styled.CheckboxWrapper onClick={(e) => {
                    e.stopPropagation()
                    onChange(campaign.uuid)
                }}
                    isMobile={isMobile}
                    key={campaign.uuid}>
                    <Styled.Checkbox
                        color="primary"
                        isMobile={isMobile}
                        key={campaign.uuid}
                        label={campaign.name}
                        labelPosition="end"
                        value={campaignIds?.some((itemCampaign) => itemCampaign.uuid === campaign.uuid)}
                        size="small"
                        disableRipple
                        onChange={() => onChange(campaign.uuid)}
                    />
                    <Styled.Color color={campaign.color} isMobile={isMobile} />
                </Styled.CheckboxWrapper>)
            })
        } else {
            options.push(<Styled.Typography key="no-campaigns">
                {t(I18N_NAMESPACE, "components.campaignSelector.noCampaigns")}
            </Styled.Typography>)
        }
        return options
    }, [
        data,
        onChange,
        onClose,
        isError,
        isMobile,
        campaignIds,
        t,
        onAddCampaignClick,
    ])

    if (isLoading) {
        return <Styled.Skeleton isMobile={isMobile} />
    }

    return <Styled.CampaignSelectorContainer isMobile={isMobile}>
        <Styled.CheckboxContainer>
            {campaignsOptions}
        </Styled.CheckboxContainer>
    </Styled.CampaignSelectorContainer>
}

